import { Camera } from "lucide-react";
import { Swiper, SwiperSlide } from "swiper/react";
import { FreeMode } from "swiper/modules";
import useProductDetail from "@/app/lib/features/productDetail/productDetail.hook";
import FallbackImage from "@/app/components/elements/FallbackImage";

interface ThumbnailSidebarProps {
  images: string[];
  activeIndex: number;
  onThumbnailClick: (index: number) => void;
  isCustomerImage: (index: number) => boolean;
  orientation?: "vertical" | "horizontal";
}

const ThumbnailSidebar = ({
  images,
  activeIndex,
  onThumbnailClick,
  isCustomerImage,
  orientation = "vertical",
}: ThumbnailSidebarProps) => {
  const { product } = useProductDetail();

  if (!product) return null;

  const alt = product.internet_product_description;

  if (orientation === "horizontal") {
    return (
      <div className="md:hidden w-full">
        {/* Pre-init genişlikler globals.css'te (.thumbnails-swiper:not(.swiper-initialized)) */}
        <Swiper
          spaceBetween={8}
          slidesPerView={4.5}
          freeMode={true}
          modules={[FreeMode]}
          className="thumbnails-swiper"
          style={{ paddingLeft: "8px", paddingTop: "8px", paddingBottom: "8px" }}
        >
            {images.map((image, i) => (
              <SwiperSlide key={i}>
                <div
                  className={`relative rounded-md overflow-hidden border-2 cursor-pointer transition-colors duration-200 ${
                    activeIndex === i
                      ? "border-main ring-1 ring-main/30"
                      : "border-gray-200"
                  }`}
                  style={{
                    transform: activeIndex === i ? "scale(1.04)" : "scale(1)",
                    transition: "transform 0.2s ease",
                  }}
                  onClick={() => onThumbnailClick(i)}
                >
                  <div className="aspect-3/4 relative">
                    <FallbackImage
                      src={image}
                      alt={alt}
                      fill
                      className="object-cover"
                      sizes="80px"
                      quality={60}
                      loading="lazy"
                    />
                  </div>
                  {isCustomerImage(i) && (
                    <div className="absolute top-1 right-1 bg-black/70 text-white p-0.5 rounded">
                      <Camera size={7} />
                    </div>
                  )}
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
      </div>
    );
  }

  return (
    <div className="hidden md:flex w-16 lg:w-20 shrink-0 flex-col gap-2 px-1 py-1">
      {images.map((image, i) => (
        <div
          key={i}
          className={`w-full rounded-md border-2 cursor-pointer shrink-0 transition-colors duration-200 hover:scale-105 transition-transform ${
            activeIndex === i
              ? "border-main ring-2 ring-main/30"
              : "border-gray-200"
          }`}
          onMouseEnter={() => onThumbnailClick(i)}
          onClick={() => onThumbnailClick(i)}
        >
          <div className="aspect-3/4 relative w-full rounded-[4px] overflow-hidden">
            <FallbackImage
              src={image}
              alt={alt}
              fill
              className="object-cover"
              sizes="80px"
              quality={60}
              loading="lazy"
            />
            {isCustomerImage(i) && (
              <div className="absolute top-1 right-1 bg-black/70 text-white p-1 rounded">
                <Camera size={8} />
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default ThumbnailSidebar;